import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Badge, Button, Card, Col, Image, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useGetSharedCartQuery, useImportSharedCartMutation } from '../../slices/cartApiSlice';
import Message from '../../components/layout/Message';
import Meta from '../../components/layout/Meta';

const SharedCartScreen = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { companiesByRuc } = useSelector((state) => state.companies);
    const { data, isLoading, error } = useGetSharedCartQuery(token, { skip: !token });
    const [importSharedCart, { isLoading: isImporting }] = useImportSharedCartMutation();

    useEffect(() => {
        if (!token) {
            toast.error('Token inválido');
        }
    }, [token]);

    const handleImport = async () => {
        try {
            await importSharedCart(token).unwrap();
            toast.success('Carrito importado correctamente');
            navigate('/cart');
        } catch (err) {
            toast.error(err?.data?.message || err.message || 'No se pudo importar el carrito');
        }
    };

    if (isLoading) {
        return (
            <>
                <Meta title="Carrito compartido" />
                <Row className="justify-content-center">
                    <Col xs={12} lg={10} xl={8}>
                        <Card className="shadow-sm border-0">
                            <Card.Body className="py-5 text-center">
                                <Spinner
                                    animation="border"
                                    role="status"
                                    style={{ color: '#b5430f' }}
                                    className="mb-3"
                                />
                                <div className="text-muted">Cargando carrito compartido...</div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </>
        );
    }

    if (error) {
        return <Message variant="danger">{error?.data?.message || error.error}</Message>;
    }

    const companyRuc = data?.companyRuc || data?.company?.ruc || data?.company;
    const companyInfo = companyRuc ? companiesByRuc?.[companyRuc] : null;
    const companyName =
        companyInfo?.tradeName ||
        data?.companyName ||
        data?.company?.tradeName ||
        data?.company?.name ||
        companyRuc;
    const orderItems = data?.orderItems || data?.items || [];
    const totalItems = orderItems.reduce((acc, item) => acc + Number(item?.qty || 0), 0);
    const estimatedTotal = orderItems.reduce(
        (acc, item) => acc + Number(item?.qty || 0) * Number(item?.price || item?.unitPrice || 0),
        0
    );

    return (
        <>
            <Meta title="Carrito compartido" />
            <Row className="justify-content-center">
                <Col xs={12} lg={10} xl={8}>
                    <Card className="shadow-sm border-0">
                        <Card.Body>
                            <h1 className="h3 fw-bold mb-2">Carrito compartido</h1>
                            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3 mb-4">
                                {companyInfo?.logoUrl ? (
                                    <Image
                                        src={companyInfo.logoUrl}
                                        alt={companyName}
                                        rounded
                                        style={{
                                            width: 64,
                                            height: 64,
                                            objectFit: 'cover',
                                            border: '1px solid #f2c5b2',
                                            backgroundColor: '#fff',
                                            padding: '0.25rem',
                                        }}
                                    />
                                ) : null}
                                <p className="text-muted mb-0">
                                    {companyName} {companyRuc ? `(${companyRuc})` : ''}
                                </p>
                            </div>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                <Badge bg="light" text="dark" className="border">
                                    Productos: {orderItems.length}
                                </Badge>
                                <Badge bg="light" text="dark" className="border">
                                    Unidades: {totalItems}
                                </Badge>
                                <Badge bg="light" text="dark" className="border">
                                    Total estimado: ${estimatedTotal.toFixed(2)}
                                </Badge>
                            </div>

                            {orderItems.length === 0 ? (
                                <Message variant="info">Este carrito compartido no tiene productos.</Message>
                            ) : (
                                orderItems.map((item) => (
                                <div key={item._id || item.product} className="d-flex align-items-center gap-3 py-3 border-bottom">
                                    <Image
                                        src={item.image || '/images/default.jpg'}
                                        alt={item.name}
                                        rounded
                                        fluid
                                        style={{ width: 64, height: 64, minWidth: 64, objectFit: 'cover' }}
                                    />
                                    <div className="flex-grow-1">
                                        <div className="fw-semibold">{item.name}</div>
                                        <div className="text-muted small">Cantidad: {item.qty}</div>
                                        <div className="fw-bold">${Number(item.price || item.unitPrice || 0).toFixed(2)}</div>
                                    </div>
                                    <div className="text-end fw-semibold">
                                        ${(Number(item.qty || 0) * Number(item.price || item.unitPrice || 0)).toFixed(2)}
                                    </div>
                                </div>
                            )))}

                            <div className="mt-4 d-grid">
                                <Button variant="primary" onClick={handleImport} disabled={isImporting || !token}>
                                    {isImporting ? 'Importando...' : 'Agregar estos productos a mi carrito'}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default SharedCartScreen;
